import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { acceptProsRequest, declineProsRequest, getInbox, getInboxSummary, type ProRequestInboxItem, type ProRequestInboxSummary } from '../../api/prosRequests'
import { useMe } from '../../hooks/useMe'
import { getToken } from '../../lib/auth'

type InboxTab = 'PENDING' | 'CLAIMED' | 'DECLINED' | 'EXPIRED'
type SortKey = 'newest' | 'expiring' | 'highest_value' | 'highest_net'

const STATUS_TABS: InboxTab[] = ['PENDING', 'CLAIMED', 'DECLINED', 'EXPIRED']

function formatHoursLeft(expiresAt?: string | null) {
  if (!expiresAt) return null
  const diffMs = new Date(expiresAt).getTime() - Date.now()
  if (diffMs <= 0) return 'Expired'
  const hrs = Math.floor(diffMs / 3600000)
  if (hrs < 24) return `Expires in ${Math.max(1, hrs)}h`
  const days = Math.floor(hrs / 24)
  return `Expires in ${days}d`
}

function urgencyBadge(row: ProRequestInboxItem): { text: string; className: string } | null {
  if (row.expiring_soon) {
    const diffMs = row.expires_at ? new Date(row.expires_at).getTime() - Date.now() : Number.MAX_SAFE_INTEGER
    if (diffMs <= 6 * 3600000) {
      return { text: 'Urgent', className: 'border-red-400/40 bg-red-500/15 text-red-100' }
    }
    return { text: 'Expiring soon', className: 'border-amber-400/40 bg-amber-500/15 text-amber-100' }
  }
  const urgency = String(row.job?.urgency || '').toUpperCase()
  if (urgency === 'ASAP' || urgency === 'TODAY') return { text: urgency, className: 'border-amber-400/40 bg-amber-500/15 text-amber-100' }
  return null
}

function sourceLabel(source?: string) {
  if (!source) return 'Discovery'
  if (source === 'invite') return 'Invite'
  if (source === 'direct') return 'Direct'
  if (source === 'returning_client') return 'Returning client'
  return 'Discovery'
}

function emptyStateCopy(tab: InboxTab): string {
  if (tab === 'PENDING') return 'No pending requests. Share your Pro card to get invites.'
  if (tab === 'CLAIMED') return 'No active claimed requests.'
  if (tab === 'DECLINED') return 'No declined requests.'
  return 'No expired requests.'
}

export default function ProsRequestsTab() {
  const authed = Boolean(getToken())
  const { me } = useMe(authed)
  const publicCardPath = useMemo(() => {
    const publicId = String((me as any)?.profile?.public_id || '').trim()
    return publicId ? `/people/i/${encodeURIComponent(publicId)}` : '/people'
  }, [me])
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = (String(searchParams.get('status') || 'PENDING').toUpperCase() as InboxTab)
  const sort = (String(searchParams.get('sort') || 'newest').toLowerCase() as SortKey)
  const q = String(searchParams.get('q') || '')

  const [items, setItems] = useState<ProRequestInboxItem[]>([])
  const [summary, setSummary] = useState<ProRequestInboxSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [actingId, setActingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const counts = useMemo(() => {
    return {
      PENDING: summary?.pending_count || 0,
      CLAIMED: summary?.claimed_count || 0,
      DECLINED: summary?.declined_count || 0,
      EXPIRED: summary?.expired_count || 0,
    }
  }, [summary])

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next, { replace: true })
  }

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const [inboxPayload, summaryPayload] = await Promise.all([
        getInbox(1, 50, STATUS_TABS.includes(tab) ? tab : 'PENDING', { sort, q }),
        getInboxSummary(30),
      ])
      setItems(Array.isArray(inboxPayload?.results) ? inboxPayload.results : [])
      setSummary(summaryPayload)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load requests.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, sort, q])

  useEffect(() => {
    if (!notice) return
    const timer = window.setTimeout(() => setNotice(null), 1800)
    return () => window.clearTimeout(timer)
  }, [notice])

  const onAccept = async (requestId: string) => {
    setActingId(requestId)
    setError(null)
    try {
      await acceptProsRequest(requestId)
      setNotice('Request accepted.')
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Unable to accept request.')
    } finally {
      setActingId(null)
    }
  }

  const onDecline = async (requestId: string) => {
    setActingId(requestId)
    setError(null)
    try {
      await declineProsRequest(requestId)
      setNotice('Request declined.')
      await load()
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Unable to decline request.')
    } finally {
      setActingId(null)
    }
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <button type="button" onClick={() => setParam('status', 'PENDING')} className="rounded-xl border border-white/10 bg-white/5 p-3 text-left hover:bg-white/10">
          <div className="text-[11px] text-white/65">Pending</div>
          <div className="text-xl font-semibold text-white">{summary?.pending_count || 0}</div>
        </button>
        <button type="button" onClick={() => { setParam('status', 'PENDING'); setParam('sort', 'expiring') }} className="rounded-xl border border-white/10 bg-white/5 p-3 text-left hover:bg-white/10">
          <div className="text-[11px] text-white/65">Expiring soon</div>
          <div className="text-xl font-semibold text-white">{summary?.expiring_soon_count || 0}</div>
        </button>
        <button type="button" onClick={() => setParam('sort', 'highest_value')} className="rounded-xl border border-white/10 bg-white/5 p-3 text-left hover:bg-white/10">
          <div className="text-[11px] text-white/65">Total potential</div>
          <div className="text-xl font-semibold text-white">{summary?.total_pixe_hint_pending || 0} PIXE</div>
        </button>
        <button type="button" onClick={() => setParam('sort', 'highest_net')} className="rounded-xl border border-white/10 bg-white/5 p-3 text-left hover:bg-white/10">
          <div className="text-[11px] text-white/65">Net after fee</div>
          <div className="text-xl font-semibold text-white">{summary?.total_net_pixe_hint_pending || 0} PIXE</div>
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((name) => (
          <button key={name} type="button" onClick={() => setParam('status', name)} className={`rounded-full border px-3 py-1.5 text-xs ${tab === name ? 'border-brand bg-brand/20 text-white' : 'border-white/15 bg-white/5 text-white/75'}`}>
            {name[0] + name.slice(1).toLowerCase()} ({counts[name] || 0})
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={q}
          onChange={(e) => setParam('q', e.target.value)}
          placeholder="Search title, client, category"
          className="min-w-[220px] flex-1 rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm outline-none"
        />
        <select value={sort} onChange={(e) => setParam('sort', e.target.value)} className="rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm">
          <option value="newest">Newest</option>
          <option value="expiring">Expiring soon</option>
          <option value="highest_value">Highest value</option>
          <option value="highest_net">Highest net</option>
        </select>
      </div>

      {loading ? <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-24 animate-pulse rounded-xl border border-white/10 bg-white/5" />)}</div> : null}
      {error ? <div className="rounded-lg border border-red-400/30 bg-red-500/10 p-2 text-sm text-red-300">{error} <button type="button" onClick={() => void load()} className="underline">Retry</button></div> : null}
      {notice ? <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/10 p-2 text-sm text-emerald-200">{notice}</div> : null}

      {!loading && items.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/70">
          <div>{emptyStateCopy(STATUS_TABS.includes(tab) ? tab : 'PENDING')}</div>
          <div className="mt-2 flex gap-2">
            <Link to={publicCardPath} className="rounded border border-white/15 bg-black/30 px-2 py-1 text-xs text-white hover:bg-white/10">
              View public card
            </Link>
            <Link to="/settings/pixe-pros?tab=profile" className="rounded border border-white/15 bg-black/30 px-2 py-1 text-xs text-white hover:bg-white/10">
              Complete profile
            </Link>
          </div>
        </div>
      ) : null}

      <div className="space-y-2">
        {items.map((row) => {
          const badge = urgencyBadge(row)
          const expiryText = formatHoursLeft(row.expires_at)
          const feePct = row.fee_pct != null ? Number(row.fee_pct) : null
          const canAct = row.status === 'PENDING'
          return (
            <div key={row.id} className="rounded-xl border border-white/10 bg-white/5 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-semibold text-white">{row.job?.title || 'Request'}</div>
                <div className="flex items-center gap-2">
                  <span className="rounded-full border border-white/15 bg-black/30 px-2 py-0.5 text-[11px] text-white/75">{sourceLabel(row.source)}</span>
                  <span className="rounded-full border border-white/15 bg-black/30 px-2 py-0.5 text-[11px] text-white/75">{row.type}</span>
                  {badge ? <span className={`rounded-full border px-2 py-0.5 text-[11px] ${badge.className}`}>{badge.text}</span> : null}
                </div>
              </div>
              <div className="mt-1 text-xs text-white/70">
                {row.job?.client?.display_name || row.job?.client?.handle ? `Client: ${row.job?.client?.display_name || ''}${row.job?.client?.handle ? ` (@${row.job.client.handle})` : ''}` : 'Client unavailable'}
              </div>
              <div className="mt-1 text-xs text-white/70">{row.job?.summary || ''}</div>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-white/70">
                <span>Budget: {row.amount_pixe_hint != null ? `${row.amount_pixe_hint} PIXE` : 'Budget not provided'}</span>
                <span>
                  Net after platform fee:
                  {row.net_pixe_hint != null ? ` ${row.net_pixe_hint} PIXE` : ' —'}
                  {feePct != null ? ` (${feePct}% fee)` : ''}
                </span>
                {expiryText ? <span>{expiryText}</span> : null}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <Link to={`/pros/requests/${encodeURIComponent(row.id)}`} className="rounded border border-white/15 bg-white/5 px-2 py-1 text-xs hover:bg-white/10">
                  View
                </Link>
                {canAct ? (
                  <button
                    type="button"
                    onClick={() => void onAccept(row.id)}
                    disabled={actingId === row.id}
                    className="rounded border border-emerald-400/30 bg-emerald-500/10 px-2 py-1 text-xs text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-60"
                  >
                    Accept
                  </button>
                ) : null}
                {canAct ? (
                  <button
                    type="button"
                    onClick={() => void onDecline(row.id)}
                    disabled={actingId === row.id}
                    className="rounded border border-red-400/30 bg-red-500/10 px-2 py-1 text-xs text-red-200 hover:bg-red-500/20 disabled:opacity-60"
                  >
                    Decline
                  </button>
                ) : null}
                <Link to={`/pros/requests/${encodeURIComponent(row.id)}#quote`} className="rounded border border-white/15 bg-white/5 px-2 py-1 text-xs hover:bg-white/10">
                  Quote
                </Link>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
